import type { ReactNode } from 'react';
import { Box, Flex, PlainButton, Stack, Text } from '@customerio/pluma-components/react';
import type { AddonCardEntry } from '../hooks/use-addon-card-entry';
import {
  creditsPurchaseCardView,
  STRIPE_LOAD_ERROR_MESSAGE,
  type CreditsPurchaseCardView,
} from '../utils/credits-purchase-form-helpers';
import { AddFundsBillingFields } from './add-funds-billing-fields';
import { CardBrands, cardLabel } from './add-funds-card-section';
import { CardFields } from './card-fields';
import * as styles from '../styles.css';

type AddonCardSectionProps = {
  entry: AddonCardEntry;
  cardError?: string;
};

const VIEWS = {
  'stripe-error': StripeLoadError,
  'saved-card': SavedCardRow,
  'stripe-loading': () => null,
  'new-card': NewCardFields,
} satisfies Record<CreditsPurchaseCardView, (props: AddonCardSectionProps) => ReactNode>;

export function AddonCardSection({ entry, cardError }: AddonCardSectionProps) {
  const view = creditsPurchaseCardView({
    isStripeLoadError: entry.stripeLoad.isError,
    isStripeLoading: entry.stripeLoad.isLoading,
    useSavedCard: entry.useSavedCard,
  });
  const View = VIEWS[view];
  return <View entry={entry} cardError={cardError} />;
}

function StripeLoadError() {
  return (
    <Text color="critical" data-test-addon-stripe-load-error>
      {STRIPE_LOAD_ERROR_MESSAGE}
    </Text>
  );
}

function SavedCardRow({ entry }: AddonCardSectionProps) {
  return (
    <Flex
      data-test-addon-saved-card
      className={styles.optionBox}
      alignItems="center"
      justifyContent="space-between"
      gap="200"
    >
      <Text className="fs-mask">{cardLabel(entry.cardDetails)}</Text>
      <PlainButton onClick={entry.onUseNewCard}>
        <Text color="accent" weight="bold" size="sm">
          Change
        </Text>
      </PlainButton>
    </Flex>
  );
}

function NewCardFields({ entry, cardError }: AddonCardSectionProps) {
  return (
    <Stack gap="150" data-test-addon-new-card>
      <CardFields
        onChange={(isComplete) => {
          entry.onOpenForm();
          entry.onCardChange(isComplete);
        }}
        isDetailsHidden={!entry.isFormOpen}
        onNumberFocus={entry.onOpenForm}
        numberLabelAccessory={<CardBrands />}
      />
      {cardError == null ? null : (
        <Text color="critical" size="sm" data-test-addon-card-error>
          {cardError}
        </Text>
      )}
      {entry.isFormOpen ? <AddFundsBillingFields form={entry} /> : null}
      {entry.hasSavedCard ? (
        <Box>
          <PlainButton onClick={entry.onUseSavedCard}>
            <Text color="accent" weight="bold" size="sm">
              Use saved card
            </Text>
          </PlainButton>
        </Box>
      ) : null}
    </Stack>
  );
}
